/** English strings for the market area. The Vietnamese file must have exactly the same keys. */
export const market = {
  area: 'Business',
  any: 'Any',
  none: 'none',
  refresh: 'Refresh',
  resetFilters: 'Reset filters',
  back: 'Back',
  reasonRequired: 'Reason (required)',
  enum: {
    auctionStatus: {
      SCHEDULED: 'Scheduled',
      OPEN: 'Live',
      CLOSED: 'Closed, awaiting settlement',
      SETTLING: 'Settling',
      SETTLED: 'Settled',
      UNSOLD: 'Unsold',
      FAILED: 'Failed',
      CANCELLED: 'Cancelled',
    },
    auctionKind: { PUBLIC: 'Public', BACKORDER_PRIVATE: 'Backorder (private)' },
    backorderStatus: {
      ACTIVE: 'Active',
      ASSIGNED: 'Assigned',
      IN_AUCTION: 'In auction',
      WON: 'Won',
      LOST: 'Lost',
      REGISTERING: 'Registering',
      CAUGHT: 'Caught',
      NOT_CAUGHT: 'Not caught',
      CANCELLED: 'Cancelled',
    },
    dropStatus: {
      SCHEDULED: 'Scheduled',
      AUCTION: 'In auction',
      ASSIGNED: 'Assigned',
      CAUGHT: 'Caught',
      NOT_CAUGHT: 'Not caught',
      NO_DEMAND: 'No demand',
      CANCELLED: 'Cancelled',
    },
    catchStatus: { PLANNED: 'Planned', POLLING: 'Polling', REGISTERING: 'Registering', CAUGHT: 'Caught', NOT_CAUGHT: 'Not caught', MANUAL_REVIEW: 'Manual review' },
    rejectReason: {
      AUCTION_NOT_OPEN: 'Auction not open',
      AUCTION_ENDED: 'Auction ended',
      BELOW_MINIMUM: 'Below minimum',
      CURRENCY_MISMATCH: 'Currency mismatch',
      NOT_ELIGIBLE: 'Not eligible',
      INSUFFICIENT_FUNDS: 'Insufficient funds',
    },
    dropSource: { OPERATOR: 'Operator', REGISTRY_NOTICE: 'Registry notice', LIFECYCLE: 'Lifecycle' },
    checkResult: { AVAILABLE: 'The name was free', UNAVAILABLE: 'The name was still taken' },
    level: { STANDARD: 'Standard', PRIORITY: 'Priority' },
    auctionEvent: {
      AUCTION_CREATED: 'Auction created',
      AUCTION_OPENED: 'Bidding opened',
      BID_ACCEPTED: 'Bid accepted',
      BID_REJECTED: 'Bid rejected',
      BIDDER_OUTBID: 'Bidder outbid',
      AUCTION_EXTENDED: 'End time extended',
      AUCTION_CLOSED: 'Bidding closed',
      WINNER_SELECTED: 'Winner selected',
      AUCTION_UNSOLD: 'Ended unsold',
      SETTLEMENT_STARTED: 'Settlement started',
      PAYMENT_SETTLED: 'Payment settled',
      DOMAIN_ASSIGNED: 'Domain handed over',
      SETTLEMENT_FAILED: 'Settlement failed',
      BACKORDER_ASSIGNED: 'Backorder assigned',
      RESULT_OVERRIDDEN: 'Result overridden',
      AUCTION_CANCELLED: 'Auction cancelled',
    },
    backorderEvent: {
      BACKORDER_PLACED: 'Backorder placed',
      BACKORDER_CANCELLED: 'Backorder cancelled',
      BACKORDER_ASSIGNED: 'Assigned to the drop',
      BACKORDER_IN_AUCTION: 'Sent to auction',
      BACKORDER_RETURNED: 'Returned to the queue',
      BACKORDER_WON: 'Won the auction',
      BACKORDER_LOST: 'Lost the auction',
      BACKORDER_REGISTERING: 'Registering the domain',
      BACKORDER_CAUGHT: 'Domain caught',
      BACKORDER_NOT_CAUGHT: 'Domain not caught',
    },
    holdStatus: { ACTIVE: 'Held', RELEASED: 'Released', CAPTURED: 'Charged', EXPIRED: 'Expired' },
    holdPurpose: {
      REGISTRATION: 'Registration',
      RENEWAL: 'Renewal',
      TRANSFER: 'Transfer',
      RESTORE: 'Restore',
      AUCTION: 'Auction',
      DROPCATCH: 'Drop-catch',
      OTHER: 'Other',
    },
    /** Every value `platform_common_compat::ActorType` writes, and nothing else. */
    actorType: { OPERATOR: 'a member of staff', CUSTOMER: 'the customer', RESELLER: 'the reseller', SYSTEM: 'the system', EXTERNAL: 'an outside party' },
  },
  format: {
    official: 'Official',
    draft: 'Unofficial draft',
    draftTitle: 'Internal UNOFFICIAL_DRAFT format: the official Registrar Data Escrow specification is not on file (ARCHITECTURE §14)',
    noticeTitle: 'Unofficial draft format.',
    notice:
      'The official ICANN Registrar Data Escrow specification (file layout, field mapping, encryption and transfer parameters) is not on file (ARCHITECTURE §14). Deposits are produced in the internal {{format}} format and are not ICANN RDE deposits.',
  },
  countdown: {
    endsIn: 'Ends in',
    startsIn: 'Starts in',
    ended: 'Ended',
    days: '{{count}}d',
    closingSoon: 'Closing soon',
  },
  auctions: {
    title: 'Auctions',
    subtitle: 'Domains we are auctioning, with the price they stand at, who is bidding and how they ended.',
    loadError: 'Could not load auctions',
    empty: 'No auctions match',
    list: 'Auctions',
    listDesc: 'Latest end time first',
    kind: 'Kind',
    status: 'Status',
    domain: 'Domain',
    domainPlaceholder: 'exact name',
    live: 'Live now',
    liveHint: 'Accepting bids',
    closingSoon: 'Closing within {{hours}} h',
    closingSoonHint: 'Live auctions ending soon',
    awaiting: 'Closed, not settled',
    awaitingHint: 'Winner to settle, or override',
    scheduled: 'Scheduled',
    scheduledHint: 'Not open yet',
    showThese: 'Show these',
    price: 'Current / final',
    reserve: 'Reserve price',
    reserveMet: 'met',
    bidCount_one: '{{count}} bid',
    bidCount_other: '{{count}} bids',
    ends: 'Ends',
    extended: 'extended ×{{count}}',
    open: 'Open auction {{domain}}',
    leader: 'Leading bidder / winner',
    noBidder: 'No bids yet',
  },
  auction: {
    liveOn: 'Live',
    liveReconnecting: 'Reconnecting…',
    title: 'Auction',
    docTitle: 'Auction {{domain}}',
    loadError: 'Could not load the auction',
    public: 'Public auction',
    private: 'Private backorder auction',
    cancel: 'Cancel auction…',
    override: 'Override result…',
    terms: 'Terms',
    startPrice: 'Start price',
    reserve: 'Reserve price',
    starts: 'Starts',
    ends: 'Ends',
    originalEnd: 'Original end',
    extensions: 'Extensions',
    drop: 'Drop',
    policyHash: 'Policy hash',
    createdBy: 'Created by',
    result: 'Result',
    currentPrice: 'Current price',
    bidsAccepted: 'Bids accepted',
    leader: 'Leading bidder',
    /** The auction ended without a winner: there is a top bid, but nobody is "leading" any more. */
    highest: 'Highest bid',
    winner: 'Winner',
    finalPrice: 'Final price',
    winningBid: 'Winning bid',
    closed: 'Closed',
    settled: 'Settled',
    order: 'Order',
    openOrder: 'Open the order',
    domain: 'Domain',
    openBackorder: 'Open the backorder',
    overrideApproval: 'Override approval',
    statusReason: 'Status reason',
    noWinner: 'No winner yet',
    reserveMet: 'Reserve met',
    reserveNotMet: 'Reserve not met',
    noReserve: 'No reserve price',
    bidsTitle: 'Bid history',
    bidsDesc: 'Accepted and rejected bids, in decision order. An accepted bid is final and the amounts are exact.',
    bidsTruncated: 'The most recent {{shown}} of {{count}} bids. An accepted bid is final and the amounts are exact.',
    noBids: 'No bids yet',
    seq: 'Seq',
    bidder: 'Bidder',
    amount: 'Amount',
    minimum: 'Minimum',
    outcome: 'Outcome',
    decided: 'Decided',
    received: 'received {{at}}',
    clientBidId: 'Client bid ID',
    bidWinner: 'Winner',
    bidLeading: 'Leading',
    bidHighest: 'Highest',
    bidAccepted: 'Accepted',
    bidRejected: 'Rejected',
    participants: 'Participants',
    openToAll: 'Open to all eligible customers',
    customer: 'Customer',
    registrant: 'Registrant',
    backorder: 'Backorder',
    joined: 'Joined',
    history: 'History',
    historyDesc: '{{count}} events, gap-free sequence',
    historyTruncated: 'The most recent {{shown}} of {{count}} events',
    correlation: 'Correlation',
  },
  cancel: {
    title: 'Cancel the auction',
    description: 'Only before settlement starts. Held funds are released; the cancellation is audited and recorded in the auction history.',
    submit: 'Cancel auction',
    busy: 'Cancelling…',
  },
  override: {
    title: 'Override the result (four-eyes)',
    description:
      'Overriding a result needs two operators. You request the approval, a different operator approves or rejects it, then the override is executed with the approved request ID. Every step is audited.',
    step1: 'Request approval',
    step1Body: 'The approver sees exactly what the override does.',
    what: 'Override',
    void: 'Void the result (no sale, release every hold)',
    award: 'Award to another accepted bid',
    bid: 'Bid',
    request: 'Request approval',
    requesting: 'Requesting…',
    requested: 'Approval requested:',
    requestedNext: 'A different operator must approve it.',
    step2: 'A second operator decides',
    step2Body: 'You cannot approve your own request.',
    noPending: 'No pending approval for this auction.',
    requestedBy: 'requested by',
    decisionReason: 'Decision reason (required)',
    approve: 'Approve',
    reject: 'Reject',
    step3: 'Execute with the approved request ID',
    step3Body: 'Filled in automatically when an approval is approved here.',
    approvalId: 'Approved request ID (required)',
    executeReason: 'Reason (required, audited)',
    execute: 'Execute override',
    executing: 'Executing…',
  },
  backorders: {
    title: 'Backorders',
    subtitle: 'Customers waiting for a name to drop, and every drop we expect with the attempt to catch it.',
    dropTimesNote: 'Drop times are ours: a member of staff enters them, or they come from a registry notice. No registry sends us a list of dropping names, so a name can drop at a time nobody recorded.',
    view: 'View',
    backorders: 'Backorders',
    drops: 'Dropping domains',
    status: 'Status',
    domain: 'Domain',
    domainPlaceholder: 'exact name',
    loadBackordersError: 'Could not load backorders',
    loadDropsError: 'Could not load drops',
    noBackorders: 'No backorders match',
    noDrops: 'No drops match',
    customer: 'Customer',
    level: 'Level',
    priority: 'Priority',
    price: 'Price',
    auction: 'Auction',
    placed: 'Placed',
    expectedDrop: 'Expected drop',
    source: 'Source',
    dropCatch: 'Catch attempt',
    for: 'for',
    registry: 'Registry',
    openBackorder: 'Open the backorder for {{domain}}',
    openDrop: 'Open the drop for {{domain}}',
  },
  backorder: {
    title: 'Backorder',
    docTitle: 'Backorder {{domain}}',
    loadError: 'Could not load the backorder',
    request: 'The request',
    customer: 'Customer',
    registrant: 'Registrant',
    registry: 'Registry',
    domain: 'Domain',
    level: 'Level',
    priority: 'Priority rank',
    queue: 'Place in the queue',
    queuePosition: '{{position}} of {{count}} waiting for this name',
    price: 'Price agreed',
    placed: 'Placed',
    updated: 'Last change',
    nameservers: 'Nameservers',
    agreement: 'Agreement accepted',
    statusReason: 'Why',
    id: 'ID',
    hold: 'Money set aside',
    holdAmount: 'Amount',
    holdPurpose: 'Reserved for',
    holdExpires: 'Held until',
    holdClosed: 'Closed',
    holdNote: 'The customer cannot spend this amount while the backorder is live. It is charged only if we catch the name.',
    drop: 'Expected drop',
    noDrop: 'No drop date has been recorded for this name yet.',
    openDrop: 'Open the drop',
    auction: 'Auction',
    openAuction: 'Open the auction',
    winner: 'Winner',
    finalPrice: 'Final price',
    bids: 'Bids',
    noAuction: 'This backorder never went to auction.',
    attempt: 'Catch attempt',
    window: 'Catch window',
    interval: 'One availability check every {{ms}} ms',
    checks: 'Checks sent',
    lastCheck: 'Last answer',
    outcome: 'Outcome',
    finished: 'Finished',
    noAttempt: 'No catch attempt has been planned yet.',
    history: 'History',
    historyDesc: 'Every change to this backorder, oldest first',
    noHistory: 'Nothing has happened yet.',
  },
  drop: {
    title: 'Dropping domain',
    docTitle: 'Drop {{domain}}',
    loadError: 'Could not load the drop',
    details: 'The drop',
    domain: 'Domain',
    registry: 'Registry',
    expected: 'Expected drop',
    source: 'Where the time comes from',
    sourceReference: 'Reference',
    statusReason: 'Why',
    recorded: 'Recorded',
    recordedBy: 'Recorded by',
    updated: 'Last change',
    id: 'ID',
    auction: 'Auction',
    openAuction: 'Open the auction',
    winner: 'Winner',
    finalPrice: 'Final price',
    bids: 'Bids',
    noAuction: 'No auction was held for this name.',
    attempt: 'Catch attempt',
    attemptFor: 'Catching for',
    window: 'Catch window',
    interval: 'One availability check every {{ms}} ms',
    checks: 'Checks sent',
    lastCheck: 'Last answer',
    outcome: 'Outcome',
    amount: 'Amount set aside',
    finished: 'Finished',
    noAttempt: 'No catch attempt has been planned yet.',
    queue: 'Backorders waiting',
    queueDesc: 'Highest priority first; the top one is assigned when the name drops',
    noQueue: 'Nobody has backordered this name.',
    customer: 'Customer',
    level: 'Level',
    priority: 'Priority',
    price: 'Price',
    status: 'Status',
    placed: 'Placed',
    open: 'Open the backorder of {{customer}}',
  },
  bo: {
    show: 'Show these',
    loadError: 'Backorders could not be loaded',
    noGuaranteeTitle: 'A backorder does not guarantee the name',
    noGuarantee: 'The expiry date is not the drop date. Each drop time comes from a registry notice, the lifecycle or a member of staff, and another registrar may register the name first.',
    stage: {
      discover: 'Expected drops',
      discoverHint: 'Recorded drop times',
      request: 'Waiting requests',
      requestHint: 'Funds held per request',
      catch: 'Catching',
      catchHint: 'Checks at drop time',
      allocate: 'Allocated',
      allocateHint: 'One request, or an auction',
    },
    overview: {
      title: 'Backorder centre',
      subtitle: 'Names about to be released, the customers waiting for them, the catch at release time and the result.',
      open: 'Open backorders',
      openHint: 'Waiting, allocated, in auction or registering',
      soon: 'Dropping within 48 hours',
      soonHint: 'Expected time, not a promise',
      catching: 'Catch attempts running',
      catchingHint: 'Planned, polling or registering',
      won: 'Caught or won',
      wonHint: 'Registered for the customer, or won in the auction',
      pipeline: 'Backorder pipeline',
      pipelineHint: 'How requests move from an expected drop to a result. Allocation follows the backorder rules in force.',
      rowScheduled: 'Waiting for the drop',
      rowAssigned: 'Allocated to one request',
      rowAuction: 'Several requests → auction',
      rowCaught: 'Caught',
      rowNotCaught: 'Not caught',
      ofDrops: '{{n}} / {{total}} drops',
      tasks: 'Priority work',
    },
    task: {
      soon: 'Dropping within 24 hours',
      soonHint: 'Check the drop time and its source',
      review: 'Catch attempts needing review',
      reviewHint: 'The result is not clear; do not charge or release by hand',
      auction: 'Backorders in an auction',
      auctionHint: 'Several customers wanted the same name',
      notCaught: 'Not caught',
      notCaughtHint: 'The held funds were released',
    },
    drops: {
      title: 'Drop list',
      subtitle: 'Every expected drop with its source and catch attempt. Times are recorded, not predicted.',
      listHint: 'Newest expected drop first.',
      dataTitle: 'Where drop times come from',
      schedule: 'Record a drop',
      scheduleTitle: 'Record an expected drop',
      scheduleHint: 'Enter a drop time you have from the registry or another reliable source. Waiting backorders for the name are allocated when the drop comes near.',
      expected: 'Expected drop time',
      expectedHint: 'Your local time; it is stored as an exact instant.',
      reference: 'Source reference',
      referenceHint: 'Registry notice number or ticket (optional).',
      scheduled: 'The drop of {{domain}} is recorded.',
    },
    requests: {
      title: 'Backorder requests',
      subtitle: 'Each customer request with its level, price, queue place and auction.',
      statesTitle: 'States kept apart',
      statesHint: 'A request, its catch attempt and its held funds each have their own state.',
      stateRequest: 'Request',
      stateCatch: 'Catch',
      stateMoney: 'Funds',
    },
    catching: {
      title: 'Drop catching',
      subtitle: 'Every catch attempt: its window, the checks sent and the result.',
      running: 'Running',
      runningHint: 'Planned, polling or registering',
      caught: 'Caught',
      caughtHint: 'Registered for the customer',
      notCaught: 'Not caught',
      notCaughtHint: 'Window ended or someone else registered it',
      review: 'Needs review',
      reviewHint: 'The result is unclear',
      attempts: 'Catch attempts',
      attemptsHint: 'One attempt per drop, for the request that was allocated the name.',
      none: 'No catch attempts yet.',
      result: 'Result',
      openDomain: 'Open the domain',
      openOrder: 'Open the order',
      registries: 'By registry',
      registriesHint: 'Catches run on each registry’s own connection allocation for drop catching.',
      registryLine: '{{running}} running · {{caught}} caught · {{failed}} not caught',
      capacity: 'Sessions and request rate for drop catching are set per registry connection.',
      capacityLink: 'Open the registry connections',
      rulesTitle: 'How a catch is decided',
      rulesHint: 'Rules of the drop-catch worker.',
      rule: {
        window: 'Checks from the drop time to the end of the window',
        windowHint: 'Availability is checked repeatedly on the drop-catch allocation; checks never use the regular connection.',
        register: 'An available name is registered at once',
        registerHint: 'Through the normal registration, with the dossier and compliance checks.',
        capture: 'Charged only after the registry confirms',
        captureHint: 'A name that was not caught releases the held funds.',
        unknown: 'An unclear registration waits for reconciliation',
        unknownHint: 'The attempt stays in "registering" until the registry answer is known.',
      },
      note: 'Catch results come from the registry. A name missing from a drop list does not mean it was caught.',
    },
    holds: {
      title: 'Holds & releases',
      subtitle: 'Funds held for backorders and bids: what is still held, what was charged and what went back.',
      purpose: 'Held for',
      purposes: { DROPCATCH: 'Backorders', AUCTION: 'Auction bids' },
      active: 'Held now',
      captured: 'Charged',
      released: 'Released or expired',
      capped: 'Showing the latest {{n}} holds; counts and sums cover these only.',
      listHint: 'Newest first. Open a hold for its ledger entries.',
      loadError: 'Holds could not be loaded',
      none: 'No holds.',
      for: 'Name',
      backorder: 'Backorder',
      bid: 'Bid',
      amount: 'Amount',
      created: 'Held on',
      closed: 'Closed on',
      order: 'Order',
      rulesTitle: 'When funds move',
      rule: {
        hold: 'Placing a backorder holds its price',
        holdHint: 'The hold is not revenue; the wallet balance stays but cannot be spent.',
        capture: 'Charged when the name is registered',
        captureHint: 'Only after the registry confirmed the registration.',
        release: 'Released when the name is not caught',
        releaseHint: 'Also when the request is cancelled or loses the auction.',
        auction: 'In a private auction the winning bid carries the charge',
        auctionHint: 'The winner’s backorder hold is released; the bid hold is charged.',
      },
    },
    detail: {
      title: 'Backorder · {{domain}}',
      subtitle: '{{customer}} · {{level}}',
      kicker: 'Domain backorder',
      dropLine: 'Drop {{status}} · source: {{source}}',
      flow: 'After the drop',
      flowHint: 'Where this request is now.',
      summary: 'Summary',
      cancel: 'Cancel',
      cancelOpen: 'Possible while waiting',
      cancelClosed: 'No longer possible',
      openHold: 'Open the hold',
      notYetTitle: 'Caught is not yet owned',
      notYet: 'The customer holds the name once the registry confirms the registration, the charge is taken and the dossier passes.',
    },
    step: {
      placed: 'Placed',
      placedHint: 'Funds held',
      drop: 'Drop',
      dropHint: 'No drop time yet',
      allocate: 'Allocation',
      allocateAuction: 'Several requests: auction',
      allocateDirect: 'One request: assigned',
      catch: 'Catch',
      catchHint: 'Check and register',
      result: 'Result',
      resultHint: 'Caught or not',
    },
  },
  au: {
    market: {
      title: 'Auction sessions',
      all: 'All',
      view: 'View',
      cards: 'Cards',
      table: 'Table',
      openSession: 'Open',
      reserveNotMet: 'Reserve not met',
      rulesTitle: 'Where auctions come from',
      rules: 'Public auctions are created by staff. Private auctions between backorder customers are created by the system when several customers wait for the same name.',
    },
    detail: {
      title: 'Auction · {{domain}}',
      subtitle: '{{kind}} · {{id}}',
      rules: 'rules v{{version}}',
      terms: 'Rules fixed for this session',
      policy: 'Rules version',
      handover: 'Result & handover',
      settlement: 'Settlement',
    },
    flow: {
      closed: 'Bidding closed',
      winner: 'Winner selected',
      paid: 'Payment taken',
      paidHint: 'Ledger entry recorded',
      assigned: 'Domain handed over',
    },
    sessions: {
      title: 'Create & run sessions',
      subtitle: 'Create a public auction, and open or close sessions whose worker is late.',
      createTitle: 'New public auction',
      createHint: 'The rules in force are fixed into the session when it is created.',
      currency: 'Currency',
      amountHint: 'Whole amount in the currency, e.g. 500000.',
      reserve: 'Reserve price (optional)',
      reserveHint: 'Not shown to bidders; without it any bid can win.',
      startsHint: 'Empty: opens now.',
      timesInvalid: 'The end must be after the start.',
      create: 'Create auction',
      created: 'Auction created under rules v{{version}}.',
      openCreated: 'Open the new session',
      noPermission: 'Your role cannot create auctions.',
      gatesTitle: 'Before and during a session',
      gate: {
        rules: 'Rules are fixed at creation',
        rulesHint: 'Increment, extensions and settlement come from the rules version recorded with the session.',
        live: 'A live session is not edited',
        liveHint: 'It can only be closed or cancelled, each with a reason in the audit log.',
        override: 'Changing a result needs a second person',
        overrideHint: 'Voiding or awarding a closed result goes through an approval.',
        private: 'Private auctions are not created here',
        privateHint: 'They come from backorders when several customers wait for a name.',
      },
      pendingTitle: 'Sessions to run',
      pendingHint: 'Scheduled, live and closed sessions.',
      nonePending: 'No session needs a hand.',
      window: 'Start → end',
      actions: 'Actions',
    },
    bids: {
      title: 'Bids & audit',
      subtitle: 'Every bid of the latest {{n}} sessions, accepted and rejected, with the reason.',
      sessions: 'Sessions read',
      sessionsHint: 'Latest sessions that have opened',
      accepted: 'Accepted bids',
      acceptedHint: 'Each has a server sequence number',
      rejected: 'Rejected bids',
      rejectedHint: 'Kept with their reason',
      overridden: 'Results overridden',
      overriddenHint: 'After a second person’s approval',
      list: 'Bids',
      listHint: 'Newest first. A rejected bid never counts towards the price.',
      none: 'No bids match.',
      received: 'Received',
      session: 'Session',
      reason: 'Reason',
      valid: 'Valid',
      reasonsTitle: 'Rejections by reason',
      noRejects: 'No rejected bids.',
      guardsTitle: 'How bidding is protected',
      guard: {
        seq: 'One order of bids per session',
        seqHint: 'The server numbers accepted bids; clients never decide the order.',
        rejects: 'Rejected bids are recorded',
        rejectsHint: 'Late, too low, wrong currency or not eligible.',
        funds: 'Funds are held for the leading bid',
        fundsHint: 'A bid without enough balance is rejected.',
        override: 'Results change only with approval',
        overrideHint: 'A flagged session is reviewed by people; nothing is concluded automatically.',
      },
    },
    settlement: {
      title: 'Settlement',
      subtitle: 'Closed sessions: the winner, the payment and the handover, one session at a time.',
      awaiting: 'Awaiting settlement',
      awaitingHint: 'Closed, winner known',
      settling: 'Settling',
      settlingHint: 'Order placed, payment in progress',
      settled: 'Settled',
      settledHint: 'Paid and handed over',
      failed: 'Failed',
      failedHint: 'Needs a decision',
      list: 'Sessions after closing',
      listHint: 'Pick a session to see its settlement.',
      none: 'No closed sessions.',
      hasOrder: 'Order placed',
      detailTitle: 'Settlement · {{domain}}',
      hold: 'Winner’s held funds',
      openHold: 'Open the hold',
      capture: 'Charge',
      openEntry: 'Open the ledger entry',
      flow: 'Settlement steps',
      noEvents: 'No settlement event yet.',
      openSession: 'Open the session',
      noEscrowTitle: 'No escrow service',
      noEscrow: 'Settlement uses the customer wallet: the winning bid’s held funds are charged and the other bidders’ holds are released. There is no seller payout or outside escrow.',
    },
    handover: {
      title: 'Domain handover',
      subtitle: 'Names won at auction or caught for a backorder, until they are in the new holder’s account.',
      list: 'Names being handed over',
      listHint: 'Pick a name to see its steps.',
      none: 'Nothing is being handed over.',
      from: 'From',
      source: { auction: 'Auction', backorder: 'Backorder' },
      recipient: 'New holder',
      amount: 'Price',
      stageCol: 'Step',
      stage: { won: 'Won', order: 'Order placed', paid: 'Paid', assigned: 'In the account' },
      failed: 'Failed',
      flow: 'Handover steps',
      gatesTitle: 'Conditions',
      gate: {
        paid: 'Payment taken',
        paidHint: 'The held funds are charged only after the registry confirms.',
        dossier: 'Registered with a valid dossier',
        dossierHint: 'The registration runs the normal dossier and compliance checks.',
        code: 'No transfer code is shown',
        codeHint: 'The name is registered straight into the new holder’s account.',
      },
      openDrop: 'Open the drop',
      noteTitle: 'Handover is a registration',
      note: 'Names from auctions and backorders are registered for the winner by the registration workflow; there is no push between accounts or registrar transfer here.',
    },
  },
} as const;
